<template>
    <div class="price-lists">
        <div class="row">
            <div class="col-default-4 col-mobile-12">
                <z-card
                    theme="background"
                    image="/images/products/wholesale.jpg"
                    full-height
                >
                    <template v-slot:title>
                        {{ text.buyInBulk }}
                    </template>
                    <template v-slot:content>
                        {{ text.contacts }}
                    </template>
                    <template v-slot:bottom>
                        <z-button
                            tag="a"
                            href="/ru/contacts/#tabs-customers"
                            kind="text"
                            class="z-button--arrow"
                        >
                            {{ text.more }}
                            <template v-slot:right>
                                <z-icon
                                    name="btn-arrow"
                                    width="16"
                                    height="16"
                                    color="#E32112"
                                ></z-icon>
                            </template>
                        </z-button>
                    </template>
                </z-card>
            </div>
            <div class="col-default-4 col-mobile-12">
                <z-card full-height>
                    <template v-slot:title>
                        {{ text.productCatalog }}
                    </template>
                    <template v-slot:content>
                        <div class="z-card__tag u-bottom-margin--one">
                            {{ text.europeAndAsia }}
                        </div>
                    </template>
                    <template v-slot:bottom>
                        <z-filelist class="price-lists__card-files">
                            <z-filelist-item icon="file/pdf" width="24" height="24">
                                <z-link
                                    href="/files/ru/products/Evraz_catalog_2020.pdf"
                                    blank
                                    size="m"
                                >{{ text.download }}</z-link>
                            </z-filelist-item>
                        </z-filelist>
                    </template>
                    <template v-slot:bottom-right>
                        <div class="z-card__icon">
                            <img src="/images/products/steel/icon-products_catalog.png">
                        </div>
                    </template>
                </z-card>
            </div>
            <div class="col-default-4 col-mobile-12">
                <z-card
                    theme="background"
                    image="/images/products/sales.jpg"
                    full-height
                >
                    <template v-slot:title>
                        {{ text.buyAtRetail }}
                    </template>
                    <template v-slot:bottom>
                        <z-link
                            href="https://evraz.market/"
                            class="u-flex--row-center"
                            theme="white"
                            target="_blank"
                            internal
                        >evraz.market</z-link>
                    </template>
                </z-card>
            </div>
        </div>
        <filters
            :data="filters"
            :initial="initial"
            @search="onChange"
        />
        <results
            :data="list"
            :nav="nav"
            v-if="list.length"
        />
        <z-preloader v-if="loading" />
        <z-not-found
            :text="text.notFoundText"
            :title="text.notFoundTitle"
            v-if="!list.length && !loading"
        />
        <z-pagination
            show-next
            show-prev
            @change-page="onPageChange"
            v-if="list.length && !loading"
            :data="{
                pageRange: 1,
                totalItems: this.nav.count,
                totalPages: this.nav.total,
                currentPage: this.nav.current
            }"
        />
    </div>
</template>

<script>
import Results from './Results.vue'
import Filters from './Filters.vue'
import getPriceLists from '@/api/price-lists'
import { localize } from '@/utils/i18n'
import { debounce } from 'throttle-debounce'
import { queryString } from '@/utils/queryString'

export default {
    name: 'price-lists',
    components: {
        Results,
        Filters
    },
    props: {
        initial: {
            type: Object,
            default: () => ({
                type: '',
                year: ''
            })
        }
    },
    data () {
        return {
            page: 1,
            loading: false,
            params: this.initial,
            data: this.$root.app.components['price-lists'],
            filters: {},
            nav: {},
            list: [],
            text: {
                showMore: localize({
                    ru: 'Показать еще',
                    en: 'Show more'
                }),
                notFoundTitle: localize({
                    ru: 'Ничего не&nbsp;найдено',
                    en: 'No&nbsp;results found'
                }),
                notFoundText: localize({
                    ru: 'По&nbsp;Вашему запросу ничего не&nbsp;нашлось.',
                    en: 'Sorry, no&nbsp;matches were found for your request. Try changing your search terms.'
                }),
                more: localize({
                    ru: 'Подробнее',
                    en: 'More'
                }),
                download: localize({
                    ru: 'Скачать',
                    en: 'Download'
                }),
                buyInBulk: localize({
                    ru: 'Купить оптом',
                    en: 'Buy in bulk'
                }),
                buyAtRetail: localize({
                    ru: 'Купить в розницу',
                    en: 'Buy at retail'
                }),
                productCatalog: localize({
                    ru: 'Каталог продукции',
                    en: 'Product Catalog'
                }),
                contacts: localize({
                    ru: 'Контакты для покупателей',
                    en: 'Customers contacts'
                }),
                europeAndAsia: localize({
                    ru: 'Европа и Азия',
                    en: 'Europe and Asia'
                })
            }
        }
    },
    created () {
        if (this.data) {
            this.nav = this.data.nav
            this.list = this.data.items
            this.filters = this.data.filters
        }
    },
    methods: {
        onChange (data) {
            this.page = 1
            this.params = { ...data, page: this.page }
            this.send(this.clearEmptyParams(this.params))
        },
        onPageChange (page) {
            this.page = page
            this.params = { ...this.params, page: this.page }
            this.send(this.clearEmptyParams(this.params))
        },
        send: debounce(1000, function (params) {
            this.loading = true

            if (this.page === 1) this.list = []

            getPriceLists(params).then(res => {
                this.updateNav(res)
                this.updateList(res)
                this.updateFilters(res)

                queryString(params)

                this.loading = false
            })
        }),
        updateNav (res) {
            if (res.hasOwnProperty('nav')) {
                this.nav.current = res.nav.current
                this.nav.total = res.nav.total
                this.nav.count = res.nav.count
            }
        },
        updateList (res) {
            if (res.hasOwnProperty('items')) {
                this.list = res.items
            }
        },
        updateFilters (res) {
            if (res.hasOwnProperty('filters')) {
                if (Object.keys(res.filters).length) this.filters = res.filters
            }
        },
        clearEmptyParams () {
            let params = Object.assign({}, this.params)

            Object.keys(params).forEach(function (key, index) {
                if (Array.isArray(params[key])) {
                    if (!(params[key].filter(val => { return val !== null && val !== undefined && val !== '' }).length)) delete params[key]
                } else if (!params[key]) {
                    delete params[key]
                }
            })

            return params
        }
    }
}
</script>

<style lang="scss">
    .price-lists {
        &__card-files {
            margin-bottom: -4px !important;
        }

        .z-pagination {
            @include breakpoint(mobile) {
                margin-top: 10px;
            }
        }
    }
</style>
